(function () {
  "use strict";

  var ROOT_SELECTOR = "[data-nf-living-floorplan]";
  var controllers = [];

  function asArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function isTrue(value) {
    return value === "true";
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function initLivingFloorplan(root) {
    if (!root || root.dataset.nfLfpReady === "true") return null;
    root.dataset.nfLfpReady = "true";

    var stage = root.querySelector("[data-nf-lfp-stage]");
    var video = root.querySelector("[data-nf-lfp-video]");
    var sourceTemplate = root.querySelector("[data-nf-lfp-video-sources]");
    var materials = asArray(root.querySelectorAll("[data-nf-lfp-material-object]"));
    var cards = asArray(root.querySelectorAll("[data-nf-lfp-card]"));
    var openButton = root.querySelector("[data-nf-lfp-open]");
    var status = root.querySelector("[data-nf-lfp-status]");
    var handoff = root.querySelector(".nf-lfp__handoff");
    var reduceMotionQuery = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    var mobileQuery = window.matchMedia ? window.matchMedia("(max-width: 749px)") : null;
    var reducedMotion = Boolean(reduceMotionQuery && reduceMotionQuery.matches);
    var videoEnabled = isTrue(root.dataset.nfLfpVideoEnabled) && Boolean(video && sourceTemplate);
    var parallaxEnabled = isTrue(root.dataset.nfLfpParallaxEnabled) && Boolean(stage && materials.length);
    var portalEnabled = isTrue(root.dataset.nfLfpPortalEnabled);
    var intensity = clamp((parseFloat(root.dataset.nfLfpMotionIntensity) || 70) / 100, 0.2, 1);
    var visible = !("IntersectionObserver" in window);
    var destroyed = false;
    var intersectionObserver = null;
    var portalObserver = null;
    var frame = 0;
    var handoffTimer = 0;
    var resetTimer = 0;
    var focusReturnTimer = 0;
    var returnFocusArmed = false;
    var currentVideoMode = "";
    var videoHasSources = false;
    var activeMaterial = "";
    var target = { x: 0, y: 0 };
    var position = { x: 0, y: 0 };
    var velocity = { x: 0, y: 0 };
    var listeners = [];

    function listen(targetNode, eventName, handler, options) {
      if (!targetNode || !targetNode.addEventListener) return;
      targetNode.addEventListener(eventName, handler, options);
      listeners.push(function () {
        targetNode.removeEventListener(eventName, handler, options);
      });
    }

    function listenMedia(query, handler) {
      if (!query) return;
      if (query.addEventListener) {
        query.addEventListener("change", handler);
        listeners.push(function () { query.removeEventListener("change", handler); });
      } else if (query.addListener) {
        query.addListener(handler);
        listeners.push(function () { query.removeListener(handler); });
      }
    }

    function announce(message) {
      if (status) status.textContent = message || "";
    }

    function setMaterialState(key) {
      activeMaterial = key || "";
      if (activeMaterial) root.setAttribute("data-nf-lfp-active", activeMaterial);
      else root.removeAttribute("data-nf-lfp-active");

      materials.forEach(function (material) {
        material.classList.toggle("is-active", material.dataset.nfLfpMaterialObject === activeMaterial);
      });
      cards.forEach(function (card) {
        var item = card.closest("[data-nf-lfp-material-card]");
        if (item) item.classList.toggle("is-active", card.dataset.nfLfpMaterial === activeMaterial);
      });
    }

    function findFocusedCard() {
      var focused = document.activeElement && document.activeElement.closest
        ? document.activeElement.closest("[data-nf-lfp-card]")
        : null;
      return focused && root.contains(focused) ? focused : null;
    }

    function clearMaterialAfterFocus() {
      window.setTimeout(function () {
        if (destroyed) return;
        var focused = findFocusedCard();
        setMaterialState(focused ? focused.dataset.nfLfpMaterial : "");
      }, 0);
    }

    function renderParallax() {
      frame = 0;
      if (destroyed || reducedMotion || !parallaxEnabled || !visible) return;

      velocity.x = (velocity.x + (target.x - position.x) * 0.105) * 0.79;
      velocity.y = (velocity.y + (target.y - position.y) * 0.105) * 0.79;
      position.x += velocity.x;
      position.y += velocity.y;

      materials.forEach(function (material) {
        var depth = clamp(parseFloat(material.dataset.nfLfpDepth) || 0.12, 0.05, 0.4);
        var travel = 70 * depth * intensity;
        material.style.setProperty("--nf-lfp-object-x", (position.x * travel).toFixed(2) + "px");
        material.style.setProperty("--nf-lfp-object-y", (position.y * travel * 0.72).toFixed(2) + "px");
        material.style.setProperty("--nf-lfp-object-rotate", (position.x * depth * intensity * 3.2).toFixed(2) + "deg");
      });

      var unsettled = Math.abs(target.x - position.x) + Math.abs(target.y - position.y)
        + Math.abs(velocity.x) + Math.abs(velocity.y) > 0.002;
      if (unsettled) frame = window.requestAnimationFrame(renderParallax);
      else root.classList.remove("is-parallax-ready");
    }

    function requestParallaxFrame() {
      if (frame || destroyed || reducedMotion || !parallaxEnabled || !visible) return;
      root.classList.add("is-parallax-ready");
      frame = window.requestAnimationFrame(renderParallax);
    }

    function resetParallax(immediate) {
      target.x = 0;
      target.y = 0;
      if (immediate) {
        position.x = 0;
        position.y = 0;
        velocity.x = 0;
        velocity.y = 0;
        materials.forEach(function (material) {
          material.style.removeProperty("--nf-lfp-object-x");
          material.style.removeProperty("--nf-lfp-object-y");
          material.style.removeProperty("--nf-lfp-object-rotate");
        });
        root.classList.remove("is-parallax-ready");
        if (frame) window.cancelAnimationFrame(frame);
        frame = 0;
        return;
      }
      requestParallaxFrame();
    }

    function handlePointerMove(event) {
      if (event.pointerType === "touch" || reducedMotion || !visible || !stage) return;
      var rect = stage.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      target.x = clamp(((event.clientX - rect.left) / rect.width - 0.5) * 2, -1, 1);
      target.y = clamp(((event.clientY - rect.top) / rect.height - 0.5) * 2, -1, 1);
      requestParallaxFrame();
    }

    function availableSourceMode() {
      if (!sourceTemplate || !sourceTemplate.content) return "";
      var preferred = mobileQuery && mobileQuery.matches ? "mobile" : "desktop";
      if (sourceTemplate.content.querySelector('[data-nf-lfp-source="' + preferred + '"]')) return preferred;
      return sourceTemplate.content.querySelector('[data-nf-lfp-source="desktop"]') ? "desktop" : "mobile";
    }

    function clearVideoSources() {
      if (!video) return;
      asArray(video.querySelectorAll("source")).forEach(function (source) { source.remove(); });
      videoHasSources = false;
      currentVideoMode = "";
      root.classList.remove("is-video-ready");
      try { video.load(); } catch (error) { /* Browser may reject load during teardown. */ }
    }

    function loadVideoSources() {
      if (!videoEnabled || reducedMotion || !video || !sourceTemplate || !sourceTemplate.content) return false;
      var mode = availableSourceMode();
      if (!mode) {
        root.classList.add("has-video-error");
        return false;
      }
      if (videoHasSources && currentVideoMode === mode) return true;

      asArray(video.querySelectorAll("source")).forEach(function (source) { source.remove(); });
      var candidates = asArray(sourceTemplate.content.querySelectorAll('[data-nf-lfp-source="' + mode + '"]'));
      if (!candidates.length) {
        root.classList.add("has-video-error");
        return false;
      }
      candidates.forEach(function (candidate) {
        var source = document.createElement("source");
        source.src = candidate.getAttribute("src");
        if (candidate.getAttribute("type")) source.type = candidate.getAttribute("type");
        video.appendChild(source);
      });
      currentVideoMode = mode;
      videoHasSources = true;
      root.classList.remove("has-video-error", "is-video-ready");
      video.load();
      return true;
    }

    function playVideo() {
      if (!visible || document.hidden || reducedMotion || !loadVideoSources()) return;
      var playPromise = video.play();
      if (playPromise && playPromise.catch) playPromise.catch(function () { /* Poster remains visible. */ });
    }

    function handleVideoReady() {
      if (!reducedMotion) root.classList.add("is-video-ready");
    }

    function handleVideoError() {
      root.classList.remove("is-video-ready");
      root.classList.add("has-video-error");
    }

    function handleIntersection(entries) {
      var entry = entries[0];
      visible = Boolean(entry && entry.isIntersecting);
      if (visible) {
        playVideo();
        requestParallaxFrame();
      } else {
        if (video) video.pause();
        resetParallax(false);
      }
    }

    function handleVisibility() {
      if (!video) return;
      if (document.hidden) video.pause();
      else playVideo();
    }

    function handleMotionPreference() {
      reducedMotion = Boolean(reduceMotionQuery && reduceMotionQuery.matches);
      root.classList.toggle("is-reduced-motion", reducedMotion);
      if (reducedMotion) {
        if (video) video.pause();
        clearVideoSources();
        resetParallax(true);
      } else if (visible) {
        playVideo();
      }
    }

    function setVideoPoster() {
      if (!video) return;
      var poster = mobileQuery && mobileQuery.matches
        ? video.dataset.nfLfpMobilePoster
        : video.dataset.nfLfpDesktopPoster;
      if (poster && video.getAttribute("poster") !== poster) video.setAttribute("poster", poster);
    }

    function handleVideoBreakpoint() {
      setVideoPoster();
      if (!videoHasSources || reducedMotion) return;
      var nextMode = availableSourceMode();
      if (nextMode && nextMode !== currentVideoMode) {
        clearVideoSources();
        if (visible) playVideo();
      }
    }

    function setHandoffOrigin() {
      if (!handoff || !openButton || !stage) return;
      var stageRect = stage.getBoundingClientRect();
      var buttonRect = openButton.getBoundingClientRect();
      root.style.setProperty("--nf-lfp-handoff-x", (buttonRect.left - stageRect.left + buttonRect.width / 2).toFixed(1) + "px");
      root.style.setProperty("--nf-lfp-handoff-y", (buttonRect.top - stageRect.top + buttonRect.height / 2).toFixed(1) + "px");
    }

    function focusLauncher() {
      returnFocusArmed = false;
      if (destroyed || !openButton || !document.documentElement.contains(openButton)) return;
      try {
        openButton.focus({ preventScroll: true });
      } catch (error) {
        openButton.focus();
      }
    }

    function scheduleLauncherFocus(delay) {
      if (!returnFocusArmed) return;
      window.clearTimeout(focusReturnTimer);
      focusReturnTimer = window.setTimeout(focusLauncher, delay || 320);
    }

    function watchPortalClose(portal) {
      returnFocusArmed = true;
      if (!portal || !("MutationObserver" in window)) return;
      if (portalObserver) portalObserver.disconnect();
      var wasOpen = portal.classList.contains("is-open");
      portalObserver = new MutationObserver(function () {
        var isOpen = portal.classList.contains("is-open");
        if (isOpen) {
          wasOpen = true;
          return;
        }
        if (!wasOpen) return;
        portalObserver.disconnect();
        portalObserver = null;
        scheduleLauncherFocus(320);
      });
      portalObserver.observe(portal, { attributes: true, attributeFilter: ["class"] });
    }

    function handlePortalEscape(event) {
      if (event.key === "Escape" && returnFocusArmed) scheduleLauncherFocus(340);
    }

    function handlePortalCloseClick(event) {
      if (returnFocusArmed && event.target.closest && event.target.closest("[data-ffs-close]")) {
        scheduleLauncherFocus(340);
      }
    }

    function findFloorFinderOpener() {
      var floorFinder = document.querySelector("[data-nf-floorfinder]");
      return floorFinder ? floorFinder.querySelector("[data-ffs-open]") : null;
    }

    function resetHandoff() {
      window.clearTimeout(handoffTimer);
      window.clearTimeout(resetTimer);
      root.classList.remove("is-handing-off");
      if (openButton) openButton.disabled = false;
      announce("");
    }

    function openStudio() {
      if (!openButton || openButton.disabled) return;
      openButton.disabled = true;
      announce("Opening FloorFinder Studio.");
      setHandoffOrigin();
      if (portalEnabled && !reducedMotion) root.classList.add("is-handing-off");

      var delay = portalEnabled && !reducedMotion ? 420 : 0;
      handoffTimer = window.setTimeout(function () {
        if (destroyed) return;
        var opener = portalEnabled ? findFloorFinderOpener() : null;
        if (opener) {
          watchPortalClose(document.querySelector("[data-ffs-portal]"));
          openButton.disabled = false;
          opener.click();
          resetTimer = window.setTimeout(resetHandoff, 1100);
          return;
        }
        var fallback = root.dataset.nfLfpStudioUrl;
        if (fallback) window.location.assign(fallback);
        else resetHandoff();
      }, delay);
    }

    cards.forEach(function (card) {
      var activate = function () { setMaterialState(card.dataset.nfLfpMaterial); };
      listen(card, "mouseenter", activate);
      listen(card, "mouseleave", function () {
        if (!card.contains(document.activeElement)) setMaterialState("");
      });
      listen(card, "focusin", activate);
      listen(card, "focusout", clearMaterialAfterFocus);
    });

    materials.forEach(function (material) {
      var key = material.dataset.nfLfpMaterialObject;
      var image = material.querySelector(".nf-lfp__material-image");
      listen(material, "mouseenter", function () { setMaterialState(key); });
      listen(material, "mouseleave", function () {
        var focused = findFocusedCard();
        setMaterialState(focused ? focused.dataset.nfLfpMaterial : "");
      });
      if (image) {
        var markFailed = function () { material.classList.add("is-object-failed"); };
        listen(image, "error", markFailed);
        if (image.complete && !image.naturalWidth) markFailed();
      }
    });

    listen(stage, "pointermove", handlePointerMove, { passive: true });
    listen(stage, "pointerleave", function () { resetParallax(false); }, { passive: true });
    listen(openButton, "click", openStudio);
    listen(document, "keydown", handlePortalEscape, true);
    listen(document, "click", handlePortalCloseClick, true);
    listen(document, "visibilitychange", handleVisibility);
    listen(video, "loadeddata", handleVideoReady);
    listen(video, "canplay", handleVideoReady);
    listen(video, "playing", handleVideoReady);
    listen(video, "error", handleVideoError);
    listenMedia(reduceMotionQuery, handleMotionPreference);
    listenMedia(mobileQuery, handleVideoBreakpoint);

    if ("IntersectionObserver" in window) {
      intersectionObserver = new IntersectionObserver(handleIntersection, {
        rootMargin: "180px 0px",
        threshold: 0.08
      });
      intersectionObserver.observe(root);
    } else {
      playVideo();
    }

    setVideoPoster();
    handleMotionPreference();

    var controller = {
      root: root,
      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        if (intersectionObserver) intersectionObserver.disconnect();
        if (portalObserver) portalObserver.disconnect();
        if (frame) window.cancelAnimationFrame(frame);
        window.clearTimeout(handoffTimer);
        window.clearTimeout(resetTimer);
        window.clearTimeout(focusReturnTimer);
        listeners.forEach(function (remove) { remove(); });
        if (video) video.pause();
        resetParallax(true);
        setMaterialState("");
        root.classList.remove("is-handing-off", "is-video-ready", "is-parallax-ready");
        root.removeAttribute("data-nf-lfp-ready");
      }
    };

    controllers.push(controller);
    return controller;
  }

  function initAll(scope) {
    var context = scope || document;
    if (context.matches && context.matches(ROOT_SELECTOR)) initLivingFloorplan(context);
    asArray(context.querySelectorAll ? context.querySelectorAll(ROOT_SELECTOR) : []).forEach(initLivingFloorplan);
  }

  function destroyWithin(scope) {
    controllers = controllers.filter(function (controller) {
      if (scope === controller.root || (scope.contains && scope.contains(controller.root))) {
        controller.destroy();
        return false;
      }
      return true;
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initAll(document); }, { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", function (event) { initAll(event.target); });
  document.addEventListener("shopify:section:unload", function (event) { destroyWithin(event.target); });
})();
